import { components, paths } from 'src/generated/v1';
import { useQueryClient } from '@tanstack/react-query';
import { $api } from './api';

export type CaseItem = Case;
export type Case = components['schemas']['Case'];
export type CaseCreate = components['schemas']['CaseCreate'];
export type CaseSection = {
  key: string;
  label: string;
};

export const useCasesQuery = (props: paths['/cases/case']['get']['parameters']['query']) => {
  const { tabIndex, ...filteredProps } = props as any;
  return $api.useQuery('get', '/cases/case', {
    params: {
      query: filteredProps,
    },
  });
};

export const useCaseStatesQuery = () => {
  return $api.useQuery('get', '/cases/state');
};

export function invalidateCaseQueries(queryClient: ReturnType<typeof useQueryClient>) {
  const keys = [
    ['get', '/cases/case'],
    ['get', `/cases/case/{case_id}`],
  ];
  keys.forEach(key => {
    queryClient.invalidateQueries({ queryKey: key });
  });
}

export const useCaseQuery = (id: number, options?: { enabled?: boolean }) => {
  return $api.useQuery(
    'get',
    `/cases/case/{case_id}`,
    {
      params: {
        path: {
          case_id: id,
        },
      },
    },
    {
      ...options,
    },
  );
};

export const useCaseUsersQuery = () => {
  return $api.useQuery('get', `/cases/assignable_user/`);
};

export const useCaseSectionsQuery = () => {
  return $api.useQuery('get', `/cases/sections/`) as {
    data?: CaseSection[];
    isLoading: boolean;
    error: Error | null;
    refetch: () => void;
  };
};

export const useCaseMutations = () => {
  const queryClient = useQueryClient();
  const setDoneMutation = $api.useMutation('put', '/utility/bulk_action/{model}/set_done', {
    onSuccess() {
      invalidateCaseQueries(queryClient);
    },
  });

  const setCancelledMutation = $api.useMutation('put', '/utility/bulk_action/{model}/cancel', {
    onSuccess() {
      invalidateCaseQueries(queryClient);
    },
  });

  const createMutation = $api.useMutation('post', '/cases/case', {
    onSuccess() {
      invalidateCaseQueries(queryClient);
    },
  });

  const updateMutation = $api.useMutation('put', '/cases/case/{case_id}', {
    onSuccess() {
      invalidateCaseQueries(queryClient);
    },
  });

  return { setDoneMutation, setCancelledMutation, createMutation, updateMutation };
};
